import { EventType } from 'matrix-js-sdk/src';
import { useCallback } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { extractAliasAsPath } from 'modules/CreateRoomModal/utils';
import { getHomeserverFromRoomId } from 'utils/matrixUtils';

type UpdateRoomCanonicalAliasParams = {
  roomId: string;
  alias: string; // alias local part only, without "#" prefix or homeserver (e.g. "my-room-123").
  currentAlias?: string | null;
};

const useUpdateRoomCanonicalAlias = () => {
  const { matrixClient } = useMatrixContext();

  const updateRoomCanonicalAlias = useCallback(
    async ({ roomId, alias, currentAlias }: UpdateRoomCanonicalAliasParams) => {
      const trimmedAlias = alias.trim();
      const initialAlias = extractAliasAsPath(currentAlias || '');
      if (!trimmedAlias || trimmedAlias === initialAlias) return;

      const homeserver = getHomeserverFromRoomId(roomId);
      const fullAlias = `#${trimmedAlias}:${homeserver}`;

      await matrixClient.createAlias(fullAlias, roomId);
      await matrixClient.sendStateEvent(roomId, EventType.RoomCanonicalAlias, { alias: fullAlias });

      // Release the old alias so it can be claimed by another room.
      if (currentAlias) {
        const fullOldAlias = currentAlias.startsWith('#') ? currentAlias : `#${initialAlias}:${homeserver}`;
        try {
          await matrixClient.deleteAlias(fullOldAlias);
        } catch {
          // Non-fatal: old alias may already be gone or we may lack permission.
        }
      }
    },
    [matrixClient]
  );

  return { updateRoomCanonicalAlias };
};

export default useUpdateRoomCanonicalAlias;
